import React, { useEffect, useMemo } from 'react';
import { Bill, BillItem, DocumentStatus, DocumentType, CreatePaymentRequest, PaymentMethod, PaymentRecord } from './types';
import { isBillCancelled } from './billDocumentAccess';
import { IPatient } from '../../../constants/types';
import PatientInfo from './PatientInfo';
import { Box, Button, Card, Chip, Grid, Tooltip, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { formatCurrency, fullDateFromPostgresString, researcherFullName } from '../../../utils';
import { BillForm } from './BillForm';
import { TypeBillItemUpdate, documentTypeToIcon } from '.';
import { documentTypeToColor, documentTypeToString, generateBillPDFFilename } from '../../../utils/bill';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Modal from '../../../components/general/modal';
import { ColourChip } from '../../../components/general/mini_components-ts';
import { useCoverage, useInsurancesList, useResearcherDepartmentSelector, useResearchersSelector, useStatusDocument, useDepartments, useSnackBarState } from '../../../hooks';
import Loader from '../../../components/Loader';
import { IconGenerator } from '../../../components/general/mini_components';
import BillPDFDocument, { toPecForPDF } from '../../../components/pdf/documents/BillPDFDocument';
import { downloadPDF } from '../../../components/pdf/utils/pdfDownload';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SplitBillModal from './SplitBillModal';
import InsuranceClaimForm from '../InsuranceClaims/InsuranceClaimForm';
import { InsuranceClaimsApiService } from '../InsuranceClaims/services/insuranceClaimsApi';
import { InsuranceClaim, InsuranceClaimStatus } from '../InsuranceClaims/types';
import { getBillByUuidService, updateBillCoverageService, splitBillService } from '../../../services/billing';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import ChipPaymentMethod from './ChipPaymentMethod';
import PaymentRecordsDisplay from './PaymentRecordsDisplay';
import PaymentRecordsModal from './PaymentRecordsModal';
import { Snackbar, Alert } from '@mui/material';
import { useDispatch } from 'react-redux';
import { updatePatientInsuranceAction } from '../../../redux/actions/patientsActions';
import { shouldHidePatientPaymentUi } from './billPaymentUi';

interface BillViewProps {
    bill: Bill;
    billStatus: DocumentStatus;
    billType: DocumentType;
    hospitalName: string;
    billingInfo: any;
    canCreateInsuranceClaims: boolean;
    patient: IPatient,
    hasBudgets:boolean,
    languageCode: string,
    currency: string,
    canUpdateBill: boolean,
    uuidInvestigation: string,
    idBillingInfo:number,
    print: boolean,
    surveyAdditionalInfo?: any,
    withDiscount: boolean,
    onUpdateBill: (bill: Bill, typeUpdate:TypeBillItemUpdate, paymentRecord?: CreatePaymentRequest) => void,
    onChangeDocumentType: (uuidBill:string, type: DocumentType) => void,
    onCancelBill: () => void,
    canUpdateInsurance: boolean,
    canEditPharmacyPrices: boolean,
    otherDraftBills?: { uuid: string; documentNumber: number }[],
    onSplitDone?: (sourceBill: Bill) => void,
}

const BillView: React.FC<BillViewProps> = (props) => {
    const [showModalConfirm, setShowModalConfirm] = React.useState(false);
    const [showModalPDF, setShowModalPDF] = React.useState(false);
    const [showModalInsuranceClaim, setShowModalInsuranceClaim] = React.useState(false);
    const [showModalPaymentRecords, setShowModalPaymentRecords] = React.useState(false);
    const [newDocumentType, setNewDocumentType] = React.useState<DocumentType>(DocumentType.BUDGET);
    const {statusDocument, renderStatusDocument} = useStatusDocument(props.billStatus, false);
    const [patientCoverage, patientInsurance, loadingPatientCoverage] = props.patient.personalData.coverage ? useCoverage(parseInt(props.patient.personalData.coverage.toString())) : [null, null, false];
    const [insurances, loadingInsurances] = useInsurancesList();
    const [insuranceClaimInitialData, setInsuranceClaimInitialData] = React.useState<InsuranceClaim>({baseAmount: props.bill.totalGross} as InsuranceClaim);
    const [insuranceClaimMode, setInsuranceClaimMode] = React.useState<'create' | 'edit' | 'view'>('create');
    const [hasInsuranceClaim, setHasInsuranceClaim] = React.useState<boolean>(false);
    const [paymentRecords, setPaymentRecords] = React.useState<PaymentRecord[]>([]);
    const { researchers, departments: allDepartments } = useDepartments();
    const { config: pdfConfig } = usePDFConfig();
    const [generatingPDF, setGeneratingPDF] = React.useState(false);
    const researcherMap = useMemo(() => {
        const map: Record<string, any> = {};
        researchers.forEach((r) => { map[r.uuid] = r; });
        return map;
    }, [researchers]);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();
    const dispatch = useDispatch();
    const [isChangingInsurance, setIsChangingInsurance] = React.useState(false);
    const [showSplitModal, setShowSplitModal] = React.useState(false);
    const [splitting, setSplitting] = React.useState(false);

    // Only persisted items (with a DB id) can be moved between bills.
    const movableItems = useMemo(
        () => (props.bill.billItems || [])
            .filter((i) => i.id != null)
            .map((i) => ({ id: i.id as number, concept: i.concept, amount: i.amount })),
        [props.bill.billItems]
    );

    const totalBillNet = useMemo(() => {
        return props.bill.totalGross - props.bill.totalDiscount;
    }, [props.bill.totalGross, props.bill.totalDiscount]);

    const externalCoverage = useMemo(() => {
        return insuranceClaimInitialData.requestedAmount ? insuranceClaimInitialData.requestedAmount : 0;
    }, [insuranceClaimInitialData, insuranceClaimInitialData.requestedAmount]);
    const totalCost = useMemo(() => {
        return props.bill.totalGross;
    }, [props.bill.totalGross]);
    const totalPaymentPatient = useMemo(() => {
        return totalCost - externalCoverage - props.bill.totalDiscount;
    }, [totalCost, externalCoverage, props.bill.totalDiscount]);
    const totalPaymentPatientDone = useMemo(() => {
        const filteredPaymentRecords = paymentRecords.filter(rec => {
            const data = rec.paymentData as any;
            return !data || data.type !== 'insurance_payment';
        });
        return filteredPaymentRecords.reduce((sum, rec) => sum + rec.amount, 0);
    }, [paymentRecords]);
    const totalPendingPaymentPatient = useMemo(() => {
        return totalPaymentPatient - totalPaymentPatientDone;
    }, [totalPaymentPatient, totalPaymentPatientDone]);
    
    const {renderResearcherDepartmentSelector, loadingResearcherOrDepartments, 
            researcherSelected, departmentSelected, uuidDepartmentSelected, departments,
            uuidResearcherSelected, markAsErrorDepartmentCallback, markAsErrorReseacherCallback} = useResearcherDepartmentSelector(props.bill.uuidPrescribingDoctor ? props.bill.uuidPrescribingDoctor : "", props.bill.uuidDepartment ? props.bill.uuidDepartment : "" );


    useEffect(() => {
        const fetchInsuranceClaimLocal = async () => {
            if(props.bill.uuid){
                await fetchInsuranceClaim();
            }
        }
        fetchInsuranceClaimLocal();
    }, [props.bill.uuid]);

    // Fetch payment records when bill changes
    useEffect(() => {
        if (props.bill && props.bill.uuid && props.uuidInvestigation) {
            refetchPaymentRecords();
        }
    }, [props.bill?.uuid, props.uuidInvestigation]);

    const refetchPaymentRecords = async () => {
        if (props.bill && props.bill.uuid && props.uuidInvestigation) {
            try {
                const response = await getBillByUuidService(props.uuidInvestigation, props.bill.uuid);
                // Type assertion to allow paymentRecords
                setPaymentRecords((response.bill as any).paymentRecords || []);
            } catch (e) {
                setPaymentRecords([]);
            }
        }
    };

    function onCloseModal(){
        setShowModalConfirm(false);
        setShowModalPDF(false);
        setShowModalInsuranceClaim(false);
        setShowModalPaymentRecords(false);
    }

    function onUpdateBill(billItems: BillItem[], typeUpdate:TypeBillItemUpdate, paymentRecord?: CreatePaymentRequest) {
        if (isBillCancelled(props.billStatus)) {
            return;
        }
        if(props.billType === DocumentType.INVOICE && props.billStatus !== DocumentStatus.CLOSED){
            if(!researcherSelected){
                markAsErrorReseacherCallback();
                return
            }
            if(!departmentSelected && departments.length > 0){
                markAsErrorDepartmentCallback();
                return
            }
        }
        
        props.bill.status = statusDocument;
        
        props.bill.uuidDepartment = uuidDepartmentSelected;
        props.bill.uuidPrescribingDoctor = uuidResearcherSelected;
        props.bill.billItems = [...billItems];
        props.onUpdateBill(props.bill, typeUpdate, paymentRecord);
    }

    function confirmChangeDocumentType(){
        console.log("Crear documento de tipo ", documentTypeToString(newDocumentType));
        setShowModalConfirm(false);
        if(props.bill!.uuid){
            props.onChangeDocumentType(props.bill.uuid, newDocumentType);
        }
    }
    
    function convertDocument(type:DocumentType){
        console.log("Convert to ", documentTypeToString(type));
        setNewDocumentType(type);
        setShowModalConfirm(true);
    }

    function renderPrescribingDoctor(){
        if(props.billType === DocumentType.INVOICE){
            if(loadingResearcherOrDepartments){
                return <Loader />
            }
            if(props.billStatus === DocumentStatus.CLOSED){
                if (!researcherSelected && !departmentSelected) {
                    return null;
                }
                return (
                    <Card style={{margin:'10px'}}>
                        <Box padding={2}>
                            <Grid container spacing={2}>
                                {researcherSelected && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" fontWeight="bold" component="span">
                                            <Translate id="hospital.billing.prescribingDoctor" />:{' '}
                                        </Typography>
                                        <Typography variant="body2" component="span">
                                            {researcherFullName(researcherSelected)}
                                        </Typography>
                                    </Grid>
                                )}
                                {departmentSelected && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" fontWeight="bold" component="span">
                                            <Translate id="hospital.billing.department" />:{' '}
                                        </Typography>
                                        <Typography variant="body2" component="span">
                                            {departmentSelected.name}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Card>
                );
                
            }
            else{
                return (
                    <Card style={{margin:'10px'}}>
                        <Box padding={2} >  
                        {
                            renderResearcherDepartmentSelector()
                        }
                        </Box>
                    </Card>
                )
            }
            
        }
        return null;
    }

    function renderConvertButton(){
        if (isBillCancelled(props.billStatus)) {
            return (
                <Card style={{ margin: '10px' }}>
                    <Box padding={2}>
                        {renderStatusDocument()}
                    </Box>
                </Card>
            );
        }
        if(!props.hasBudgets || !patientInsurance){
            return(
                <Card style={{margin:'10px'}}>
                    <Box padding={2} >  
                     { renderStatusDocument() }
                    </Box>
                </Card>
            )
        }
        const typeButton = <>
                <Typography variant="body2" component='span'><span style={{ fontWeight: 'bold' }}><Translate id="hospital.billing.bill.type" /></span>: </Typography>
                <ColourChip rgbcolor={documentTypeToColor(props.billType)} 
                    label={<Translate id={`hospital.billing.bill.types.${documentTypeToString(props.billType)}`}/>}
                    avatar={documentTypeToIcon(props.billType)}
                    /> 
            </>
            let convertGrid = null
            const canConvertType = props.billType === DocumentType.BUDGET || props.billType === DocumentType.SUMMARY;
            if(canConvertType){
                // SHER-434: only offer the actual conversion when this source has no LIVE child yet.
                // Once a Devis/Facture has been spun off (and not cancelled), the API rejects a second
                // conversion (400 CHANGE_ALREADY_HAS_ACTIVE_CHILD). When a live child exists we render a
                // disabled chip + tooltip instead of the clickable buttons, so the user understands WHY
                // they can't convert. The buttons return once that child is cancelled.
                let convertContent;
                if(!props.bill.hasActiveRelatedChild){
                    const budgetButton = <ColourChip rgbcolor={documentTypeToColor(DocumentType.BUDGET)} avatar={documentTypeToIcon(DocumentType.BUDGET)}
                                                    label={<Translate id="hospital.billing.convert.to_budget"/>}
                                                    onClick={() => convertDocument(DocumentType.BUDGET)} />
                    const invoiceButton = <ColourChip rgbcolor={documentTypeToColor(DocumentType.INVOICE)}  avatar={documentTypeToIcon(DocumentType.INVOICE)}
                                                    onClick={() => convertDocument(DocumentType.INVOICE)}
                                                    label={<Translate id="hospital.billing.convert.to_invoice"/>} />
                    // SHER-415: from a Synthèse des actes, PAF patients may convert either to a Devis
                    // (to request external financial support from a charity/NGO/sponsor) or directly to
                    // a Facture définitive, so offer both. Insurance/mutuelle patients keep the Devis step.
                    convertContent = props.billType === DocumentType.SUMMARY
                                                ? (patientInsurance.name === "PAF"
                                                    ? <>{budgetButton}{invoiceButton}</>
                                                    : budgetButton)
                                                : invoiceButton
                }
                else {
                    convertContent = (
                        <Tooltip title={<Translate id="hospital.billing.convert.already_has_active_child" />}>
                            <span>
                                <Chip disabled label={<Translate id="hospital.billing.convert.already_converted" />} />
                            </span>
                        </Tooltip>
                    );
                }

                convertGrid = (
                    <>
                        <div>
                            <div>
                                <KeyboardArrowRightIcon />
                            </div>
                        </div>
                        <div style={{display:'flex', gap:'8px'}}>
                            {
                                convertContent
                            }
                        </div>
                    </>
                );
            }
            return (
                <Card style={{margin:'10px'}}>
                        <Box padding={2} >  
                    <div style={{'paddingBottom' : '1rem'}}>
                        <div style={{display:'flex', flexDirection:'row'}}>
                            <div >
                                { typeButton }
                            </div>
                            { convertGrid } 
                        </div>
                        {
                            renderStatusDocument()
                        }
                    </div>
                </Box>
                </Card>
            )      
    }
    
    // Helper to fetch insurance claim for this bill
    async function fetchInsuranceClaim() {
        const apiService = new InsuranceClaimsApiService();
        try {
            const claims = await apiService.getClaimsForBill(props.uuidInvestigation, props.bill.uuid!);
            if (claims && claims.length > 0) {
                
                const filteredClaim:InsuranceClaim = Object.keys(claims[0]).reduce((obj, key) => {
                    if (claims[0][key] !== null) {
                        obj[key] = claims[0][key];
                    }
                    return obj;
                }, {});
                filteredClaim.baseAmount = totalBillNet;
                setInsuranceClaimInitialData(filteredClaim);
                
                // Set mode to 'view' if claim is APPROVED or PAID, otherwise 'edit'
                const isClaimLocked = filteredClaim.status === InsuranceClaimStatus.APPROVED || 
                                     filteredClaim.status === InsuranceClaimStatus.PAID;
                setInsuranceClaimMode(isClaimLocked ? 'view' : 'edit');
                setHasInsuranceClaim(true);
            } else {
                setInsuranceClaimInitialData({baseAmount: totalBillNet} as InsuranceClaim);
                setInsuranceClaimMode('create');
                setHasInsuranceClaim(false);
            }
        } catch (e) {
            setInsuranceClaimInitialData({baseAmount: totalBillNet} as InsuranceClaim);
            setInsuranceClaimMode('create');
            setHasInsuranceClaim(false);
        }
    }

    async function handleDownloadBillPDF() {
        if (!props.bill || !pdfConfig) {
            return;
        }

        try {
            setGeneratingPDF(true);

            // Get prescribing doctor
            const prescribingDoctor = props.bill.uuidPrescribingDoctor 
                ? researchers.find((r) => r.uuid === props.bill.uuidPrescribingDoctor) 
                : undefined;

            // Get department
            const department = props.bill.uuidDepartment 
                ? allDepartments.find((d) => d.uuid === props.bill.uuidDepartment) 
                : undefined;

            // Convert InsuranceClaim to the format expected by BillPDFDocument
            const pecForPDF = toPecForPDF(insuranceClaimInitialData);

            // Create PDF document
            const pdfDocument = (
                <BillPDFDocument
                    bill={props.bill}
                    type={props.bill.type}
                    patient={props.patient}
                    uuidInvestigation={props.uuidInvestigation}
                    uuidPrescribingDoctor={props.bill.uuidPrescribingDoctor}
                    uuidDepartment={props.bill.uuidDepartment}
                    currency={props.currency}
                    locale={props.languageCode}
                    config={pdfConfig}
                    paymentRecords={paymentRecords}
                    pec={pecForPDF}
                    prescribingDoctor={prescribingDoctor}
                    department={department}
                    patientInsurance={patientInsurance}
                    patientCoverage={patientCoverage ? { name: patientCoverage.name } : null}
                />
            );

            // Generate localized filename
            const filename = generateBillPDFFilename(props.bill, props.languageCode);

            // Download PDF
            await downloadPDF(pdfDocument, filename);
            setShowModalPDF(false);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setGeneratingPDF(false);
        }
    }

    function renderModal(){
        if(showModalConfirm){
            return(
                <Modal key="modal" fullWidth medium open={true} title={<Translate id="hospital.billing.bill.view.modal.title" />} closeModal={() => onCloseModal()}
                    confirmAction={confirmChangeDocumentType} >
                    <Typography variant="body2" component='span'><Translate id="hospital.billing.bill.view.modal.message" /></Typography>
                </Modal>
            )
        }
        else if(showModalPDF){
            return (
                <Modal key="modal" maxWidth="xs" open={true} 
                    title={!props.bill ? "Create bill" : ""}  closeModal={() => onCloseModal()}>
                    {!pdfConfig ? (
                        <Loader />
                    ) : (
                        <Grid container spacing={2} justifyContent="center" style={{ padding: '16px' }}>
                            <Grid item xs={12} style={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={handleDownloadBillPDF}
                                    disabled={generatingPDF}
                                    fullWidth
                                >
                                    <Translate id="hospital.billing.bill.download"/>
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </Modal>
            )
        }
        else if(showModalInsuranceClaim){
            return (
                <Modal key="modal" open={true} maxWidth="sm" 
                    title={<Translate id="hospital.billing.insurance_claims.modal.title" />} 
                    closeModal={() => onCloseModal()}>
                    <InsuranceClaimForm
                        uuidInvestigation={props.uuidInvestigation}
                        uuidBill={props.bill.uuid!}
                        mode={insuranceClaimMode}
                        initialData={insuranceClaimInitialData}
                        availableInsurances={insurances || []}
                        currency={props.currency}
                        languageCode={props.languageCode}
                        onSuccess={async (claim) => {
                            // Close modal first
                            onCloseModal();
                            // Wait a bit for the backend to process, then fetch and update
                            setTimeout(async () => {
                                await fetchInsuranceClaim();
                            }, 500);
                        }}
                        onCancel={() => onCloseModal()}
                        isReadOnly={insuranceClaimMode === 'view'}
                    />
                </Modal>
            )
        }
        else if(showModalPaymentRecords){
            return (
                <PaymentRecordsModal
                    open={true}
                    paymentRecords={paymentRecords}
                    paymentMethods={props.billingInfo.paymentMethods || []}
                    researcherMap={researcherMap}
                    currency={props.currency}
                    languageCode={props.languageCode}
                    onClose={() => setShowModalPaymentRecords(false)}
                />
            );
        }
        return null;
    }

    // Calculate total paid
    const totalPaid = paymentRecords.reduce((sum, rec) => sum + rec.amount, 0);

    const handleSplit = async (payload: { billItemIds: number[]; target: { type: 'new' } | { type: 'existing'; uuid: string } }) => {
        if (!props.bill.uuid) return;
        setSplitting(true);
        try {
            const response = await splitBillService(props.uuidInvestigation, props.bill.uuid, payload);
            setShowSnackbar({ show: true, message: 'hospital.billing.split_bill.success', severity: 'success' });
            if (props.onSplitDone) {
                props.onSplitDone(response.sourceBill);
            }
        } finally {
            // Re-throw on failure is implicit: splitBillService rejects, the await above
            // throws past this finally, and SplitBillModal's onConfirm catch shows the error.
            setSplitting(false);
        }
    };

    const handleChangeCoverage = async (coverageId: number) => {
        if (!props.uuidInvestigation || !props.bill?.uuid) return;
        setIsChangingInsurance(true);
        try {
            await updateBillCoverageService(props.uuidInvestigation, props.bill.uuid, coverageId);
            setShowSnackbar({ show: true, message: 'hospital.billing.bill.success.updated', severity: 'success' });
            // Update patient coverage in Redux store
            dispatch(updatePatientInsuranceAction(props.uuidInvestigation, props.patient.uuid, coverageId));
            // Refresh bill info
            if (props.bill && props.bill.uuid && props.uuidInvestigation) {
                const response = await getBillByUuidService(props.uuidInvestigation, props.bill.uuid);
                if (response && response.bill) {
                    if (props.onUpdateBill) {
                        props.onUpdateBill(response.bill, TypeBillItemUpdate.BillItems);
                    }
                }
                if (typeof refetchPaymentRecords === 'function') {
                    refetchPaymentRecords();
                }
            }
        } catch (e) {
            setShowSnackbar({ show: true, message: 'hospital.billing.bill.error.permission', severity: 'error' });
        } finally {
            setIsChangingInsurance(false);
        }
    };

    if(isChangingInsurance){
        return <Loader />
    }
    return (
        <>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={showSnackbar.show}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity={showSnackbar.severity as any} sx={{ width: '100%' }}>
                    <Translate id={showSnackbar.message} />
                </Alert>
            </Snackbar>
            {renderModal()}
            <Card style={{margin:'10px'}}>
                <Box padding={2} >  
                    <PatientInfo 
                        patient={props.patient} 
                        languageCode={props.languageCode} 
                        canUpdateInsurance={props.canUpdateInsurance && props.billStatus === DocumentStatus.DRAFT}
                        isLoadingInsurance={isChangingInsurance}
                        rightSide={
                            <Grid item xs={6}  >
                                <Typography variant="body2"><span style={{ fontWeight: 'bold' }}><Translate id="hospital.billing.bill.num_bill" /></span>: {props.bill.documentNumber}</Typography>
                                <Typography variant="body2"><span style={{ fontWeight: 'bold' }}><Translate id="hospital.billing.bill.date" /></span>: {fullDateFromPostgresString(props.languageCode, props.bill.createdAt)}</Typography>
                                <Typography variant="body2"><span style={{ fontWeight: 'bold' }}><Translate id="hospital.billing.bill.type" /></span>: <Translate id={`hospital.billing.bill.types.${documentTypeToString(props.billType)}`}/></Typography>
                                {/* SHER-416: a Synthèse des actes (SUMMARY) can be previewed/printed in both
                                    Modifiable (DRAFT) and Non Modifiable (CLOSED) states. Budgets/invoices keep
                                    the original "only when CLOSED" gating. The PDF itself stamps the status
                                    (see BillPDFDocument) so a provisional synthèse isn't mistaken for final. */}
                                {(props.billStatus == DocumentStatus.CLOSED || props.billType === DocumentType.SUMMARY) && (
                                    <>
                                        <Box display="flex" alignItems="center" mb={1}>
                                            <Typography component="span" variant="body2" fontWeight="bold" mr={1}>
                                                <Translate id="hospital.billing.bill.view_pdf" />
                                            </Typography>
                                            <Button onClick={() => setShowModalPDF(true)} size="small" style={{ minWidth: 0, padding: 6 }}>
                                                <IconGenerator type="pdf" />
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Grid>
                        }
                        onChangeCoverage={handleChangeCoverage}
                    />
                </Box>
            </Card>
            {props.billStatus === DocumentStatus.DRAFT && !isBillCancelled(props.billStatus) && props.canUpdateBill && movableItems.length > 0 && (
                 <Card style={{margin:'10px'}}>
                    <Box padding={2} >  
                        <Button variant="outlined" onClick={() => setShowSplitModal(true)}>
                            <Translate id="hospital.billing.split_bill.button" />
                        </Button>
                    </Box>
                 </Card>
            )}
            <SplitBillModal
                open={showSplitModal}
                onClose={() => setShowSplitModal(false)}
                items={movableItems}
                otherDraftBills={props.otherDraftBills || []}
                currency={props.currency}
                languageCode={props.languageCode}
                onConfirm={handleSplit}
                loading={splitting}
            />

            <Card style={{margin:'10px'}}>
                <Grid container justifyContent="center" padding={2}>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                <Translate id="hospital.billing.bill.total_cost" /> : 
                            </Typography>
                            <Chip
                                label={formatCurrency(totalCost, props.currency, props.languageCode)}
                                color="primary"
                                style={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: '#fff', marginRight: 8 }}
                                size="small"
                            />
                        </Box>
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                <Translate id="hospital.billing.bill.total_discount" /> : 
                            </Typography>
                            <Chip
                                label={formatCurrency(props.bill.totalDiscount, props.currency, props.languageCode)}
                                color="primary"
                                style={{ fontWeight: 'bold', backgroundColor: '#339085', color: '#fff', marginRight: 8 }}
                                size="small"
                            />
                        </Box>
                        {props.billingInfo?.params?.taxEnabled && (props.bill.totalTax || 0) > 0 && (
                            <>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                        <Translate id="hospital.billing.bill.total_tax" /> :
                                    </Typography>
                                    <Chip
                                        label={formatCurrency(props.bill.totalTax || 0, props.currency, props.languageCode)}
                                        color="primary"
                                        style={{ fontWeight: 'bold', backgroundColor: '#ff9800', color: '#fff', marginRight: 8 }}
                                        size="small"
                                    />
                                </Box>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                        <Translate id="hospital.billing.bill.total_net" /> :
                                    </Typography>
                                    <Chip
                                        label={formatCurrency(props.bill.totalNet || 0, props.currency, props.languageCode)}
                                        color="primary"
                                        style={{ fontWeight: 'bold', backgroundColor: '#7b1fa2', color: '#fff', marginRight: 8 }}
                                        size="small"
                                    />
                                </Box>
                            </>
                        )}
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                <Translate id="hospital.billing.bill.pec" /> :
                            </Typography>
                            <Chip
                                label={formatCurrency(externalCoverage, props.currency, props.languageCode)}
                                color="primary"
                                style={{ fontWeight: 'bold', backgroundColor: '#3579f8', color: '#fff', marginRight: 8 }}
                                size="small"
                            />
                            {(props.canCreateInsuranceClaims && !isBillCancelled(props.billStatus)) &&
                                <Button
                                    variant="text"
                                    onClick={() => setShowModalInsuranceClaim(true)}
                                    size="small"
                                    style={{ minWidth: 0, padding: 6 }}
                                >
                                    { !hasInsuranceClaim ? (
                                        <IconGenerator type="add" />
                                    ) : insuranceClaimMode === 'view' ? (
                                        <IconGenerator type="view" />
                                    ) : (
                                        <IconGenerator type="edit" />
                                    )}   
                                </Button>
                            }
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                            <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                <Translate id="hospital.billing.bill.total_to_pay" /> : {formatCurrency(totalPaymentPatient, props.currency, props.languageCode)}
                            </Typography>
                        </Box>
                        <PaymentRecordsDisplay
                            paymentRecords={paymentRecords}
                            currency={props.currency}
                            languageCode={props.languageCode}
                            onViewPaymentRecords={() => setShowModalPaymentRecords(true)}
                            showViewButton={props.billStatus !== DocumentStatus.DRAFT}
                        />
                        <Box display="flex" alignItems="center">
                            <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                <Translate id="hospital.billing.bill.pending_payment" /> :
                            </Typography>
                            <Chip
                                label={formatCurrency(totalPendingPaymentPatient, props.currency, props.languageCode)}
                                color="primary"
                                style={{ fontWeight: 'bold', backgroundColor: totalPendingPaymentPatient > 0 ? '#f44336' : '#4CAF50', color: '#fff', marginRight: 8 }}
                                size="small"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Card>
            {renderConvertButton()}
            {renderPrescribingDoctor()}
            <BillForm key="Billview" canUpdateBill={props.canUpdateBill} canEditPharmacyPrices={props.canEditPharmacyPrices} patient={props.patient} currency={props.currency}
                uuidInvestigation={props.uuidInvestigation} idBillingInfo={props.idBillingInfo} bill={props.bill}
                print={props.print} withDiscount={props.withDiscount} surveyAdditionalInfo={props.surveyAdditionalInfo}
                onBillItemsValidated={(billItems:BillItem[]) => onUpdateBill(billItems, TypeBillItemUpdate.BillItems)}
                onUpdateBillItemStatus={(billItems:BillItem[], paymentRecord?: CreatePaymentRequest) => onUpdateBill(billItems, TypeBillItemUpdate.BillItemsStatus, paymentRecord)}
                onCancelBill={props.onCancelBill}
                hidePatientPaymentUi={shouldHidePatientPaymentUi(props.billStatus, totalPendingPaymentPatient)}
                onPaymentSuccess={refetchPaymentRecords}
            />
        </>
    );
};

export default BillView;
